import React from 'react';
import { Clock, DollarSign, Users, BarChart3, Zap, Shield } from 'lucide-react';
import { motion } from 'framer-motion';
import { useModal } from '../contexts/ModalContext';

const benefits = [
  {
    icon: DollarSign,
    title: 'Discover Revenue Opportunities',
    description: 'Learn how to identify and capture hidden revenue streams in your salon',
    stat: '28% average revenue increase'
  },
  {
    icon: Clock,
    title: 'Save Time on Operations',
    description: 'See how automation eliminates 15+ hours of manual work every week',
    stat: '15+ hours saved weekly'
  },
  {
    icon: Users,
    title: 'Improve Team Efficiency',
    description: 'Watch how streamlined processes boost staff productivity and satisfaction',
    stat: '46% better engagement'
  },
  {
    icon: BarChart3,
    title: 'Get Data-Driven Insights',
    description: 'Understand how real-time analytics help you make profitable decisions',
    stat: 'Real-time reporting'
  },
  {
    icon: Zap,
    title: 'Automate Customer Journey',
    description: 'See how automated marketing and follow-ups increase client retention',
    stat: '4-5x higher retention'
  },
  {
    icon: Shield,
    title: 'Risk-Free Implementation',
    description: 'Learn about our proven onboarding process and success guarantee',
    stat: '14-day money back guarantee'
  }
];

const WhyShouldAttend_Intl: React.FC = () => {
  const { handleBookNowClick } = useModal();
  
  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
            Why Should You Attend This Demo?
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            In just 30 minutes, you'll discover exactly how Zylu can transform your salon's operations and profitability
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => {
            const IconComponent = benefit.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="bg-red-100 p-4 rounded-lg w-fit mb-6">
                  <IconComponent className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-black mb-4">{benefit.title}</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">{benefit.description}</p>
                <div className="bg-red-50 px-4 py-2 rounded-lg">
                  <span className="text-red-600 font-semibold text-sm">{benefit.stat}</span>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="bg-black text-white rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="text-3xl font-bold mb-4">
              What You'll Walk Away With
            </h3>
            <div className="grid md:grid-cols-3 gap-6 mt-8 mb-8">
              <div>
                <div className="text-2xl font-bold text-red-400 mb-2">✓</div>
                <p>Custom implementation roadmap for your salon</p>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-400 mb-2">✓</div>
                <p>ROI calculator showing your potential savings</p>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-400 mb-2">✓</div>
                <p>Free consultation on your current challenges</p>
              </div>
            </div>
            <button
              onClick={handleBookNowClick}
              className="bg-red-600 hover:bg-red-700 text-white font-bold py-4 px-8 rounded-lg shadow-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl text-xl"
            >
              Book Your Demo Now
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyShouldAttend_Intl;
